import { Plus } from "lucide-react";
import { MaskTitle, Reveal, SectionLabel } from "./motion";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/context/LangContext";

const ORDER = { main: 0, official: 1, partner: 2, supplier: 3 };

const SponsorTile = ({ s, i }) => {
  const inner = s?.logo ? (
    <img src={s.logo} alt={s?.name} className="max-h-[45%] max-w-[72%] object-contain transition-transform duration-500 group-hover:scale-105" />
  ) : (
    <span className="font-display text-2xl font-black uppercase md:text-3xl">{s?.name}</span>
  );
  const cls = "group relative grid aspect-[3/2] place-items-center";
  return s?.url ? (
    <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-tile-${i}`} className={cls}>{inner}<TierTag t={s?.tier} /></a>
  ) : (
    <div data-testid={`sponsor-tile-${i}`} className={cls}>{inner}<TierTag t={s?.tier} /></div>
  );
};

const TierTag = ({ t: tier }) => {
  const { t } = useLang();
  return <span className="label-mono absolute left-3 top-3 text-[11px] text-zinc-600">{t.sponsors.tiers[tier] || "Partner"}</span>;
};

export const Sponsors = ({ sponsors, onBecomePartner }) => {
  const list = [...(Array.isArray(sponsors) ? sponsors : [])].sort((a, b) => (ORDER[a?.tier] ?? 4) - (ORDER[b?.tier] ?? 4));
  const fillers = Math.max(4 - list.length, (4 - (list.length % 4)) % 4) || (list.length === 0 ? 4 : 0);

  const become = () => { onBecomePartner?.(); scrollToId("contatti"); };
  const { t, lang } = useLang();

  return (
    <section id="sponsor" data-testid="sponsors-section" className="bg-paper px-5 py-24 text-ink md:px-10 md:py-36">
      <div className="mx-auto max-w-[1600px]">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <SectionLabel index="06" dark={false}>{t.sponsors.label}</SectionLabel>
            <MaskTitle key={lang} lines={t.sponsors.title} className="mt-6 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl lg:text-9xl" />
          </div>
          <p className="max-w-md text-sm leading-relaxed text-zinc-700 md:text-base">
            {t.sponsors.intro}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4" data-testid="sponsors-grid">
          {list.map((s, i) => (
            <Reveal key={s?.id || i} delay={Math.min(i * 0.05, 0.3)}><SponsorTile s={s} i={i} /></Reveal>
          ))}
          {Array.from({ length: fillers }).map((_, i) => (
            <Reveal key={`f${i}`} delay={Math.min((list.length + i) * 0.05, 0.3)}>
              <button onClick={become} data-testid={`sponsor-placeholder-${i}`}
                className="group grid aspect-[3/2] w-full place-items-center border border-dashed border-ink/30 transition-colors duration-500 hover:border-trident hover:bg-trident hover:text-white">
                <span className="flex flex-col items-center gap-2">
                  <Plus className="h-6 w-6 transition-transform duration-500 group-hover:rotate-90" />
                  <span className="label-mono text-[11px]">{t.sponsors.your_brand}</span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-4">
          <button onClick={become} data-testid="sponsor-cta-btn"
            className="group flex w-full flex-col items-start justify-between gap-6 bg-ink p-8 text-left text-white transition-colors duration-500 hover:bg-trident md:flex-row md:items-center md:p-12">
            <span className="font-display text-4xl font-black uppercase leading-none md:text-6xl">{t.sponsors.cta}</span>
            <span className="label-mono rounded-full border border-current px-6 py-3">{t.sponsors.cta_btn}</span>
          </button>
        </Reveal>
      </div>
    </section>
  );
};
